/* Tilbud til Mediability – norsk (bokmål), standardspråk. Et digitalt tilbudsbrev med et produkt inni: brev → én mulighet (systemet i seks visninger) → seks steg → KI → Visma → leveranse → pris → veien videre. */
import { CASES } from "../cases";
import { MEDIABILITY as EN } from "./mediability";

const P = "/tilbod/mediability/";
const T = "/tilbod/kviteberg/team/";
const byId = Object.fromEntries(CASES.map((c) => [c.id, c]));

export const UI_NB = {
  proposal: "Tilbud",
  draft: "Utkast",
  pdf: "Last ned PDF",
  pdfBusy: "Lager PDF …",
  skip: "Hopp til innholdet",
  to: "Til",
  from: "Fra",
  confidential: "Konfidensielt",
  validUntil: "Gyldig til",
  page: "Side",
  date: "Dato",
  reference: "Referanse",
  contact: "Kontakt",
  proposalTo: "Tilbud til",
  fileWord: "Tilbud",
  prototype: "Fungerende prototype med fiktive kundedata. Klikk på fanene.",
  yes: "Ja",
  no: "—",
  contents: "Innhold",
};

export const MEDIABILITY_NO = {
  ...EN,
  lang: "nb",
  ui: UI_NB,
  title: "Komplekst salg.\nSamlet i ett system.",
  to: { company: "Mediability AS", person: "David Besseberg", role: "Ledergruppen", address: "Norge", fill: false },
  from: { company: "Kviteberg AS", person: "Martin Kviteberg", role: "Ansvarlig for leveransen", email: "martin@kviteberg.no", address: "Kvinnherad, Norge", photo: P + "martin.jpg" },

  hero: {
    sub: "Mediability selger komplekse løsninger. Vi foreslår et skreddersydd, KI-drevet system som samler krav, løsning, kalkyle, tilbud og ordregrunnlag i én arbeidsflyt – integrert med Visma Business.",
  },

  /* under brevet: fra henvendelse til ferdig tilbud – stegene til venstre, kundens tilbud til høyre */
  showcase: {
    eyebrow: "Fra henvendelse til tilbud",
    title: "Et komplett tilbud\npå fem minutter.",
    lede: "Studio Bergen: 2,3 MNOK fordelt på ni produktgrupper. En omfattende forespørsel blir lest, strukturert, kalkulert og gjort klar for utsending på få minutter.",
    steps: [
      { t: "0:00", title: "Henvendelsen kommer inn" },
      { t: "0:40", title: "Krav og behov hentes ut" },
      { t: "1:30", title: "Produkter og løsning settes sammen" },
      { t: "3:00", title: "Kalkylen ferdigstilles og kontrolleres" },
      { t: "4:30", title: "Tilbudet genereres" },
      { t: "5:00", title: "Klart til å sende kunden" },
    ],
    url: "tilbud.mediability.no/q/studio-bergen",
    tour: "Blar automatisk · rull for å bla selv",
    tourTouch: "Blar automatisk",
  },

  live: {
    eyebrow: "Prototype",
    title: "Prøv prototypen selv.",
    lede: "Den kjører med fiktive kundedata. Logg inn, åpne Studio Bergen og gå hele veien fra henvendelse til tilbud.",
    hint: "Fiktive kundedata",
    openFull: "Åpne prototypen",
    ctaSub: "Fullskjerm · starter på innloggingssiden",
    open: "Åpne i eget vindu",
    restart: "Start på nytt",
    close: "Lukk",
    showControls: "Vis kontroller",
    loading: "Laster prototypen …",
  },

  letter: {
    greeting: "Hei Lars,",
    close: "Vennlig hilsen",
    p: [
      "Takk for muligheten til å presentere et tilbud til Mediability.",
      "Vi har gjort vårt beste for å sette oss inn i salgsprosessen deres, og foreslår et skreddersydd system som samler kundehenvendelser, krav, løsningsdesign, kalkyle og tilbud i én arbeidsflyt – med integrasjon mot Visma Business.",
      "KI brukes der den kan redusere manuelt arbeid, mens pris, margin og godkjenninger fortsatt styres av Mediability.",
      "For å gjøre forslaget konkret har vi allerede bygget en fungerende prototype med fiktive data. Den er ment som et utgangspunkt for å utvikle den endelige løsningen sammen med dere.",
    ],
  },

  idea: {
    eyebrow: "Systemet",
    title: "Ett arbeidsområde.\nHele veien til ordre.",
    p: ["Innboks, muligheter, løsning, kalkyle, tilbud og dashbord i ett system – integrert med Visma Business. Muligheten bygges opp én gang og følger kunden gjennom hele salget."],
    caption: "Pipelinen i prototypen. Fiktive kundedata.",
    /* the system in three views – the mockup switches between them */
    views: [
      { key: "inbox", label: "Inbox", hint: "Nye henvendelser", caption: "Innboksen i prototypen: KI leser e-posten, finner krav og åpne spørsmål, oppretter muligheten og foreslår svar. Fiktive kundedata." },
      { key: "pipeline", label: "Opportunities", hint: "Aktive salg", caption: "Pipelinen i prototypen: én mulighet beveger seg fra henvendelse til ordre i Visma Business. Fiktive kundedata." },
      { key: "opportunity", label: "Opportunity", hint: "Studio Bergen", caption: "Én mulighet i prototypen: krav, løsning, kalkyle og tilbud på samme side – til ordren ligger i Visma Business. Fiktive kundedata." },
      { key: "workspace", label: "AI Workspace", hint: "KI-assistent", caption: "AI Workspace i prototypen: henvendelsen limes inn, KI strukturerer behov, foreslår løsning, kalkulerer og skriver tilbudet. Fiktive kundedata." },
      { key: "quotes", label: "Quotes", hint: "Tilbud", caption: "Tilbudene i prototypen: hva som venter på svar, hvor lenge, og hva som er akseptert. Fiktive kundedata." },
      { key: "overview", label: "Dashboard", hint: "Oversikt", caption: "Oversikten i prototypen: pipeline, det som trenger oppmerksomhet, dagens avtaler og forslag til neste trekk. Fiktive kundedata." },
    ],
    stages: ["Henvendelse", "Behov", "Løsning", "Kalkyle", "Tilbud", "Forhandling", "Ordre"],
  },

  proto: {
    title: "Slik kan det se ut.",
    p: "Vi har allerede bygget en fungerende prototype for å gjøre diskusjonen konkret. Dataene er fiktive, men arbeidsflyten viser retningen.",
    caption: "Mulighetene i prototypen, med fiktive kundedata.",
    img: P + "pipeline.jpg",
    coverImg: P + "opp-overview.jpg",
  },

  flow: {
    title: null,
    head: "Seks steg",
    steps: [
      ["Henvendelse", "E-post, nettskjema, møte eller anbud opprettes som en ny mulighet."],
      ["Behov", "Krav, åpne spørsmål og manglende informasjon struktureres."],
      ["Løsning", "Produkter, tjenester og leverandører settes sammen til riktig løsning."],
      ["Kalkyle", "Kost, salgspris, rabatt og margin beregnes etter definerte regler."],
      ["Tilbud", "Den godkjente løsningen blir et digitalt og versjonsstyrt tilbud."],
      ["Ordre", "Akseptert tilbud overføres til Visma Business uten dobbeltregistrering."],
    ],
  },

  ai: {
    title: "KI som gjør forarbeidet.",
    p: ["KI skal gjøre arbeidsflyten raskere – ikke systemet mer komplisert.", "Den kan blant annet:"],
    list: ["lese kundehenvendelser og anbud", "identifisere krav og åpne spørsmål", "strukturere teknisk informasjon", "foreslå produkter og løsninger", "lage førsteutkast til tilbud", "hente frem relevant historikk"],
    after: "Menneskene tar beslutningene. Systemet gjør forarbeidet og holder oversikten.",
    example: {
      from: "E-post fra kunde",
      to: "KI finner 7 krav og 2 åpne spørsmål",
      img: P + "opp-requirements.jpg",
      pdfCaption: "Kravlisten i prototypen, utledet fra én e-post. Fiktive data.",
      caption: "AI Workspace i prototypen: fra tomt arbeidsområde og én innlimt e-post til behov, løsning, kalkyle og sendt tilbud. Fiktive data.",
    },
  },

  visma: {
    title: "Visma Business blir værende\nder det er best.",
    p: [
      "Vi foreslår ikke å erstatte Visma Business som økonomi- og ordresystem.",
      "Det nye systemet blir arbeidsflaten for den kommersielle prosessen – og stedet der kunder, produkter, leverandører, løsninger, kalkyler og tilbud bygges og vedlikeholdes.",
      "Når en løsning er godkjent, overføres nødvendige data automatisk til Visma Business for ordre, fakturering og videre økonomisk behandling.",
    ],
    line: ["Mediability Workspace", "Visma Business"],
    under: ["Kunder · produkter · leverandører · krav · løsninger · priser · kalkyler · tilbud", "Salgsordre · fakturering · økonomi"],
    after: "Målet er at brukerne skal slippe å vedlikeholde den samme informasjonen i flere systemer. Data registreres og bearbeides i den nye løsningen og overføres til Visma Business når de skal inngå i den økonomiske prosessen.",
    note: "Eksakt integrasjon og hvilke data som skal synkroniseres i hver retning avklares ved prosjektstart ut fra Mediabilitys Visma Business-oppsett.",
  },

  delivery: {
    title: "Fra prototype til produksjon\npå åtte uker.",
    p: "Prototypen gjør at vi kan starte med det viktigste: tilpasse løsningen til Mediability, koble riktige data og integrasjoner, teste på reelle salgscaser – og sette systemet i drift.",
    phases: [
      ["Uke 1–2", "Forstå og låse", "Vi går gjennom dagens salgsflyt, roller, godkjenninger, produkt- og leverandørdata og eksisterende Salesforce-data. Samtidig avklarer vi hvilke data som skal flyte videre til Visma Business."],
      ["Uke 3–4", "Bygge og integrere", "Systemet tilpasses Mediability med produktkatalog, kalkyle, marginregler, tilbud, Microsoft 365-innlogging og første integrasjon mot Visma Business."],
      ["Uke 5–6", "Teste på ekte saker", "Relevante Salesforce-data migreres, og en liten pilotgruppe bruker systemet i reelle salg. Arbeidsflyt, KI-støtte og regler finjusteres etter faktisk bruk."],
      ["Uke 7–8", "Gå i produksjon", "Siste justeringer gjennomføres før opplæring, produksjonssetting og overgang til det nye systemet."],
    ],
    after: "Deretter følger 30 dager med tett oppfølging etter oppstart.",
    includedTitle: "Dette er med i første leveranse",
    includedItems: [
      ["Salg og kunder", "Pipeline, muligheter, kunder og kontakter"],
      ["Behov og løsning", "Krav, avklaringer og løsningsbygger"],
      ["Produkter og leverandører", "Produktkatalog, leverandører, kost og pris"],
      ["Kalkyle og kontroll", "Margin, rabatt og godkjenningsregler"],
      ["Tilbud", "Digitale tilbud, PDF og versjonshistorikk"],
      ["KI", "Henvendelser, anbud, krav, løsning og tilbud"],
      ["Integrasjoner", "Visma Business og Microsoft 365"],
      ["Oppstart", "Salesforce-migrering, opplæring og 30 dagers oppfølging"],
    ],
    outside: "Prosjektleveranse, service, kundeportal og andre arbeidsprosesser kan senere bygges på som egne moduler.",
  },

  price: {
    title: "Pris og gjennomføring",
    intro: [
      "Basert på løsningen som er skissert i dette tilbudet, estimerer vi første produksjonsversjon til 150 000–200 000 kr eks. mva.",
      "Dette er vårt beste estimat basert på dagens forståelse av Mediabilitys behov. Før oppstart går vi gjennom løsningen sammen, avklarer nøyaktig scope og gir et konkret tilbud med endelig pris, leveranse og fremdriftsplan.",
    ],
    cards: [
      { key: "dev", value: "150–200 000 kr", label: "Estimert prosjektpris", text: "Første produksjonsversjon, basert på foreslått leveranse." },
      { key: "licence", value: "15 000 kr / mnd", label: "Plattformlisens", text: "Alle brukere inkludert. Drift, vedlikehold, support og normal KI-bruk." },
      { key: "time", value: "Ca. 8 uker", label: "Estimert gjennomføring", text: "Fra prosjektstart til første produksjonsversjon." },
    ],
    stepsTitle: "Fra estimat til konkret tilbud",
    steps: [
      ["Avklare", "Vi går gjennom prototypen og prioriterer hva første versjon skal inneholde."],
      ["Definere", "Funksjonalitet, integrasjoner, datamigrering og avgrensninger spesifiseres."],
      ["Tilby", "Mediability mottar et konkret tilbud med avtalt scope, pris og fremdriftsplan før oppstart."],
    ],
    basisTitle: "Estimatet tar utgangspunkt i",
    basis: [
      ["Arbeidsflyt", "Pipeline, kunder, krav, løsning, kalkyle, godkjenning og tilbud."],
      ["Data og integrasjoner", "Produkt- og leverandørdata, relevant Salesforce-migrering, Microsoft 365 og Visma Business."],
      ["Oppstart", "Pilot, opplæring, produksjonssetting og 30 dagers oppfølging."],
    ],
    basisNote: "Endelig omfang kan justeres i fellesskap før tilbudet ferdigstilles.",
    licenceTitle: "Plattformlisens",
    licence: [
      "Plattformlisensen er 15 000 kr per måned eks. mva. for hele Mediability, alle brukere inkludert.",
      "Den dekker drift i EU/EØS, backup og overvåking, vedlikehold, sikkerhetsoppdateringer, normal KI-bruk og support. Lisensen starter ved produksjonssetting og har tre måneders oppsigelsestid.",
    ],
    vismaTitle: "Visma Business-integrasjon",
    vismaNote:
      "Estimatet inkluderer etablering av integrasjon mot Mediabilitys eksisterende Visma Business-miljø innenfor det integrasjonsomfanget som beskrives etter teknisk avklaring. Eventuelle særskilte tredjepartskostnader, tilpasninger eller begrensninger i eksisterende Visma-oppsett avklares før endelig tilbud.",
    /* PDF only */
    big: [
      ["150–200 000 kr", "Estimert prosjektpris"],
      ["15 000 kr / mnd", "Plattformlisens"],
      ["Ca. 8 uker", "Estimert gjennomføring"],
    ],
    termsTitle: "Vilkår i korte trekk",
    terms: [
      "Mediability eier egne data og kan få dem eksportert i brukbare formater ved opphør.",
      "Kviteberg eier plattformen og kildekoden. Mediability har bruksrett så lenge lisensen løper. Eventuell ordning for kildekodekontinuitet kan avtales separat.",
      "Drift i EU/EØS, rollebasert tilgang, logging og Microsoft 365-innlogging er inkludert. Databehandleravtale inngås ved behov.",
      "Eksisterende Visma Business-lisenser og nødvendig tilgang til Visma Business er Mediabilitys ansvar.",
      "Arbeid utenfor avtalt omfang spesifiseres og godkjennes før det gjennomføres.",
    ],
  },

  about: {
    title: "Forretningsforståelse møter teknisk håndverk.",
    p: [
      "Kviteberg AS er et norsk konsulent- og programvareselskap basert i Kvinnherad. Vi kombinerer rådgivning med egen utvikling – slik at analysen og systemet som bygges, kommer fra samme hånd.",
      "Bak selskapet står Martin, Bertine, Per Ove og Sigrid Kviteberg – et lite team med kort vei fra beslutning til leveranse, og et nettverk av spesialister der oppdraget krever det.",
      "Vi kommer fra familien bak Adonis, forretningskritisk programvare brukt av rederier verden over siden 1988.",
    ],
    checks: ["Rådgivning, utvikling og drift i én leveranse", "Dere eier egne data – full åpenhet", "Base i Kvinnherad, leverer i hele Norge"],
    team: [
      { name: "Martin Kviteberg", role: "Grunnlegger · Rådgivning og utvikling", img: T + "martin.jpg" },
      { name: "Bertine Kviteberg", role: "Rådgiver", img: T + "bertine.jpg" },
      { name: "Per Ove Kviteberg", role: "Strategisk partner", img: T + "per-ove.jpg" },
      { name: "Sigrid Kviteberg", role: "Rådgiver", img: T + "sigrid.jpg" },
    ],
    site: { label: "kviteberg.no", url: "https://kviteberg.no" },
    refsLabel: "Utvalgte leveranser",
    refs: [
      { id: "travelease", k: "TravelEase.AI" },
      { id: "vestmaskin", k: "Vest Maskinservice" },
      { id: "digihome", k: "DigiHome" },
    ],
    get refItems() {
      return this.refs.map((r) => ({ ...byId[r.id], ...r }));
    },
  },

  next: {
    title: "Veien videre",
    p: ["Dersom Mediability ønsker å gå videre, starter vi med en kort arbeidsøkt med relevante personer fra salg og økonomi.", "Her går vi gjennom dagens salgsflyt, tester prototypen mot reelle salgscaser og avklarer datamigrering, Visma Business-integrasjon og innholdet i første produksjonsversjon."],
    agendaTitle: "Arbeidsøkten vil typisk dekke:",
    agenda: [
      ["30 min", "Dagens prosess fra kundehenvendelse til ordre"],
      ["30 min", "Prototypen mot reelle salgscaser"],
      ["30 min", "Data, Visma Business og første pilot"],
    ],
    after: "Deretter kan vi bekrefte endelig omfang og plan for oppstart.",
    subject: "Tilbud KV-2026-MB-01 – Mediability",
  },
};
